/** Bound the entire request, including authentication interception and body reads. */
export async function requestJson(path, options = {}, {
  fetchImpl = globalThis.fetch,
  timeoutMs = String(options.method || "GET").toUpperCase() === "GET" ? 12_000 : 45_000,
  onInvalidSession
} = {}) {
  const controller = new AbortController();
  const method = String(options.method || "GET").toUpperCase();
  let timeoutId;
  let abortListener;
  const cancellation = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      controller.abort();
      const error = new Error(method === "GET"
        ? "The dashboard request timed out. Refresh will retry automatically."
        : "The action timed out before the app could confirm it. Check the latest status before trying it again.");
      error.name = "TimeoutError";
      reject(error);
    }, timeoutMs);
    abortListener = () => {
      controller.abort();
      const error = new Error("The request was cancelled.");
      error.name = "AbortError";
      reject(error);
    };
    if (options.signal?.aborted) abortListener();
    else options.signal?.addEventListener("abort", abortListener, { once: true });
  });
  try {
    return await Promise.race([cancellation, (async () => {
      const response = await fetchImpl(path, {
        ...options,
        signal: controller.signal,
        cache: "no-store",
        headers: { ...(options.body ? { "Content-Type": "application/json" } : {}), ...(options.headers || {}) }
      });
      if (response.status === 401 && response.headers.get("X-Shopify-Retry-Invalid-Session-Request") === "1" && onInvalidSession?.()) {
        throw new Error("Refreshing the Shopify session. Please wait a moment.");
      }
      let body;
      try { body = await response.json(); }
      catch (error) {
        if (response.ok) throw new Error("The app returned an incomplete response. The dashboard will refresh again.");
        body = {};
      }
      if (!response.ok) throw new Error(body.error || `The request could not be completed (HTTP ${response.status}).`);
      return body;
    })()]);
  } finally {
    clearTimeout(timeoutId);
    options.signal?.removeEventListener("abort", abortListener);
  }
}
